import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';

/**
 * Reusable IconSelector component for selecting icons in a circular grid
 * @param {object} props
 * @param {Array} props.icons - Array of icon options with {name, icon, displayName} structure
 * @param {string} props.selectedIcon - Currently selected icon name
 * @param {function} props.onIconChange - Callback when icon is selected
 * @param {string} props.label - Label for the icon selector (optional)
 */
const IconSelector = ({ 
  icons = [], 
  selectedIcon, 
  onIconChange, 
  label = "Icons" 
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box>
      <Typography variant="body1" fontWeight="500" mb={1.5}>
        {label}
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {icons.map((iconOption) => {
          const IconComponent = iconOption.icon;
          const isSelected = selectedIcon === iconOption.name;
          
          return (
            <Tooltip key={iconOption.name} title={iconOption.displayName} arrow>
              <Box
                onClick={() => onIconChange(iconOption.name)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: colors.grey[isSelected ? 200 : 100],
                  cursor: 'pointer',
                  border: isSelected ? `2px solid ${colors.primary[400]}` : 'none',
                  outline: isSelected ? `2px solid #fff` : 'none',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    bgcolor: colors.grey[200]
                  }
                }}
              >
                <IconComponent size={24} strokeWidth={2} style={{
                  color: isSelected ? colors.primary[500] : colors.grey[600]
                }} />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default IconSelector;
